/*
 * The library documents behind an "Ask the Library" answer, read off the
 * reply's library tool calls (`case_agent.tools.mcp_research`): every hit a
 * search returned and every document a lookup opened. The list under the
 * answer shows the ones the answer cites (or opened); when it names none,
 * the top hits, so the attorney still sees what the library returned.
 *
 * No React or IO here so the rules stay unit-testable.
 */
import type { AuthorityWeight, ResearchSourceId } from "./types";

/** Each library tool, by the source it reads (`research_scope.SOURCE_TOOLS`). */
export const LIBRARY_TOOL_SOURCE: Readonly<
  Record<string, ResearchSourceId | null>
> = {
  search_cases: "case_law",
  get_case_by_citation: "case_law",
  search_statutes: "statutes",
  get_statute_by_citation: "statutes",
  find_in_statute: "statutes",
  search_references: "references",
  get_reference_by_citation: "references",
  find_in_reference: "references",
  search_unpublished_cases: "unpublished",
  search_district_court_decisions: "district",
  get_district_court_decision: "district",
  get_regulatory_updates: "updates",
  get_library_document: null
};

export const isLibraryTool = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(LIBRARY_TOOL_SOURCE, name);

export type ChatSource = {
  id: string;
  title: string;
  citation: string | null;
  weight: AuthorityWeight | null;
  /** The answer names it, or a lookup opened it. */
  cited: boolean;
};

/** A finished library call: its tool name and output. */
export type LibraryCall = { tool: string; output: unknown };

/** How many returned hits show when the answer cites none. */
export const MAX_UNCITED = 6;

type Item = Record<string, unknown>;

const isRecord = (value: unknown): value is Item =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const parsed = (output: unknown): unknown => {
  if (typeof output !== "string") return output;
  try {
    return JSON.parse(output) as unknown;
  } catch {
    return null;
  }
};

/** The kind a tool's hits are when the hit leaves it out. */
const KIND_OF_SOURCE: Partial<Record<ResearchSourceId, string>> = {
  statutes: "statute",
  references: "reference",
  updates: "update"
};

/**
 * What an authority is worth, as `research_library_service.weight` labels
 * it for the Research page; the tool fills in a kind the hit omits.
 */
export const weightOf = (item: Item, tool: string): AuthorityWeight => {
  const source = LIBRARY_TOOL_SOURCE[tool] ?? null;
  const kind = text(item.type) || (source ? KIND_OF_SOURCE[source] ?? "" : "");
  const level = text(item.authorityLevel);
  const status = text(item.precedentialStatus);
  if (["statute", "regulation"].includes(kind)) return "controlling";
  if (["statute", "regulation"].includes(level)) return "controlling";
  if (["reference", "update"].includes(kind) || status === "guidance")
    return "guidance";
  if (
    ["nonprecedential", "unpublished"].includes(status) ||
    ["unpublished", "district"].includes(level) ||
    source === "unpublished" ||
    source === "district"
  )
    return "persuasive";
  if (["precedential", "binding"].includes(status)) return "precedent";
  return source === "case_law" ? "precedent" : "persuasive";
};

const normalized = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

/** Whether the answer names the document by its citation or full title. */
const namedIn = (
  answer: string,
  doc: Pick<ChatSource, "title" | "citation">
): boolean => {
  if (!answer) return false;
  if (doc.citation && answer.includes(normalized(doc.citation))) return true;
  const title = normalized(doc.title);
  return title.length >= 8 && answer.includes(title);
};

/**
 * The documents to list under an answer, cited first. `answer` is the
 * reply's text. Empty when no library call returned a document.
 */
export const chatSourcesOf = (
  calls: readonly LibraryCall[],
  answer: string
): ChatSource[] => {
  const said = normalized(answer);
  const byId = new Map<string, ChatSource>();
  const add = (item: unknown, tool: string, opened: boolean) => {
    if (!isRecord(item)) return;
    const id = text(item.id);
    const title = text(item.title) || text(item.citation);
    if (!id || !title) return;
    const doc = {
      id,
      title,
      citation: text(item.citation) || null,
      weight: weightOf(item, tool)
    };
    const seen = byId.get(id);
    const cited = opened || namedIn(said, doc) || Boolean(seen?.cited);
    byId.set(id, { ...(seen ?? doc), cited });
  };
  for (const { tool, output } of calls) {
    const out = isLibraryTool(tool) ? parsed(output) : null;
    if (isRecord(out) && !out.error) {
      if (isRecord(out.document)) add(out.document, tool, true);
      if (Array.isArray(out.hits))
        for (const hit of out.hits) add(hit, tool, false);
    }
  }
  const all = [...byId.values()];
  const cited = all.filter((d) => d.cited);
  return cited.length ? cited : all.slice(0, MAX_UNCITED);
};
